import React, { useEffect } from 'react';
import { Formik, FormikProps } from 'formik';
import { Box } from '@material-ui/core';
import { compact, find, get, head, size } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { Role, Venue } from '../../../../generated/graphql';
import { InputField, MultipleAutocompleteField, SelectField } from '../../../../components/forms/fields';
import FormWrapper from '../../../../components/forms/FormWrapper';
import { Options } from '../../../../components/forms/fields/types';
import { mapLocalizedEnumOptions, sanitizeFormValues } from '../../../../utils';
import { useUser } from '../../../../utils/hooks/auth';
import { Typography } from '../../../../ui-kit';
import { employeeValidationSchema } from './validation';
import { EmployeeFormProps, EmployeeFormValues } from './types';
import { roleToHintMap } from './utils';

const getOptionSelected = (option: Options, value: Options) => option.id === value.id;

const mapDefaultVenueSelector = (
    venues: Venue[],
    formikProps: FormikProps<EmployeeFormValues>
) => compact<Venue>(venues.map(venue => find({ id: venue.id }, formikProps.getFieldProps('venueIds').value)));

const OrderedRoles: Record<Role, string> = {
    [Role.Admin]: Role[Role.Admin],
    [Role.WarehouseGeneralManager]: Role[Role.WarehouseGeneralManager],
    [Role.WarehouseManager]: Role[Role.WarehouseManager],
    [Role.WarehouseUser]: Role[Role.WarehouseUser],
    [Role.User]: Role[Role.User],
};

const EmployeeForm = ({
    onSubmit,
    isLoading,
    venues,
    submitText,
    isUpdate = false,
    initialValues = {
        email: '',
        venueIds: [],
        role: Role.User,
        defaultVenueId: '',
        password: '',
        pin: '',
        firstName: '',
        lastName: ''
    },
}: EmployeeFormProps) => {
    const isVenueSelectDisplayed = size(venues) >= 2;
    const { t } = useTranslation();
    const { isAdmin, user } = useUser();
    const { params } = useRouteMatch<{ id?: string }>();

    const { id: employeeId } = params;
    const isCurrentEmployee = employeeId === user.employeeId;

    const roles = mapLocalizedEnumOptions(t)({ Role: OrderedRoles });

    const handleSubmit = async (values: EmployeeFormValues) => {
        const sanitizedValues = sanitizeFormValues(values);
        return onSubmit(sanitizedValues);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={employeeValidationSchema(isUpdate)}
        >
            {formikProps => (
                <FormWrapper disabled={isLoading} submitText={submitText} isSubmitting={formikProps.isSubmitting}>
                    <InputField<EmployeeFormValues>
                        name="firstName"
                        label={t('First Name')}
                        formikProps={formikProps}
                        required
                    />
                    <InputField<EmployeeFormValues>
                        name="lastName"
                        label={t('Last Name')}
                        formikProps={formikProps}
                        required
                    />
                    <InputField<EmployeeFormValues>
                        name="email"
                        autoComplete='none'
                        label={t('Email')}
                        formikProps={formikProps}
                        required
                    />
                    <InputField<EmployeeFormValues>
                        name="pin"
                        autoComplete='none'
                        type="pin"
                        label={t('Pin')}
                        formikProps={formikProps}
                    />
                    <InputField<EmployeeFormValues>
                        name="password"
                        autoComplete='none'
                        type="password"
                        label={t('Password')}
                        formikProps={formikProps}
                    />
                    {isAdmin && !isCurrentEmployee
                        ? (
                            <SelectField
                                helpertext={t(...roleToHintMap[formikProps.values.role])}
                                name='role'
                                label={t('Role')}
                                formikProps={formikProps}
                                options={roles}
                                variant='outlined'
                            />
                        ) : null}
                    {isAdmin && isVenueSelectDisplayed ?
                        (
                            <>
                                <Box my={2} display="flex" flexDirection="column">
                                    <Typography variant="h5">{t('Venues')}</Typography>
                                    <MultipleAutocompleteField
                                        name="venueIds"
                                        label={t('Venues')}
                                        options={venues}
                                        getOptionSelected={getOptionSelected}
                                    />
                                </Box>
                                <DefaultVenue
                                    formikProps={formikProps}
                                    venues={venues}
                                />
                            </>
                        )
                        : null}
                </FormWrapper>
                )}
        </Formik>
    );
};

export default EmployeeForm;

interface DefaultVenueProps {
    formikProps: FormikProps<EmployeeFormValues>;
    venues: Venue[];
}
const DefaultVenue = ({ formikProps, venues }: DefaultVenueProps) => {
    const { values: { venueIds: selectedVenueIds, defaultVenueId }, setFieldValue } = formikProps;
    const isDefaultVenueDisplayed = selectedVenueIds ? size(selectedVenueIds) >= 2 : false;
    const { t } = useTranslation();
    useEffect(() => {
        /* Probaly needs to be adstracted, almost the same is used on BE */
        const belongs = find<Options>(venue => venue.id === defaultVenueId)(selectedVenueIds);
        if (!belongs) {
            setFieldValue('defaultVenueId', get('id')(head(selectedVenueIds)));
        }
    }, [selectedVenueIds, defaultVenueId, setFieldValue]);


    return isDefaultVenueDisplayed ? (
        <SelectField
            name='defaultVenueId'
            label={t('Default Venue')}
            formikProps={formikProps}
            options={mapDefaultVenueSelector(venues, formikProps)}
            variant='outlined'
        />
    ): null;
};
